import {
  getBindableFields,
  defineBindablePropertyFromFieldPath,
  toBindingPath,
  formatGQLField,
  type FieldPath,
} from '@packages/systems/cms/gateway/shared';

import {COLLECTION_TYPES} from '@packages/systems/dynamo/constants';
import {
  DEFAULT_SKU_SLUG,
  ORDER_ITEMS_BINDING_CONTEXT_EXTERNAL_KEY,
} from '@packages/systems/commerce/constants';
import {getFieldBySlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import {DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {
  buildProductItemRefField,
  buildSkuItemRefField,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {fieldSlug as formatFieldSlug} from '@packages/systems/dynamo/utils/SlugUtils';

import {
  CART_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT_ID,
  ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
  ORDER_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT,
} from './identifiers';

import {
  groupCollectionFieldsByProductSection,
  getBindableSkuFields,
  sortProductAndSkuFields,
} from './products-and-skus';

import type {CollectionRecordType as Collection} from '@packages/systems/dynamo/records/CollectionRecord';

import type {
  BindingContextTypeDef,
  BindingContextRelationshipDef,
  BindablePropertiesDef,
  BindablePropertyDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';

export const orderItemType: BindingContextTypeDef = {
  entity: 'Entity',
  id: ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
  label: 'Products',
  pluralLabel: 'Cart Items',
  singularLabel: 'Cart Item',
  iconName: 'product',
  externalKey: ORDER_ITEMS_BINDING_CONTEXT_EXTERNAL_KEY,
};

const PRODUCT_FIELD_SLUGS_TO_GQL_FIELDS_MAPPING: Record<string, string> = {
  category: 'category',
  'default-sku': 'defaultSku',
  'ec-product-type': 'productType',
  'sku-properties': 'skuProperties',
};

const SKU_FIELD_SLUGS_TO_GQL_FIELDS_MAPPING: Record<string, string> = {
  'compare-at-price': 'compareAtPrice',
  'ec-sku-billing-method': 'ecSkuBillingMethod',
  'ec-sku-subscription-plan': 'ecSkuSubscriptionPlan',
  'ec-sku-inventory-quantity': 'ecSkuInventoryQuantity',
  'main-image': 'mainImage',
  'more-images': 'images',
  'sku-values': 'skuValues',
  width: 'width',
  length: 'length',
  height: 'height',
  weight: 'weight',
  sku: 'sku',
};

const ECOMMERCE_QUERY_PATH_MAPPER = {
  product: PRODUCT_FIELD_SLUGS_TO_GQL_FIELDS_MAPPING,
  sku: SKU_FIELD_SLUGS_TO_GQL_FIELDS_MAPPING,
};

function formatGQLFieldResolver(
  field: Field,
  target: 'sku' | 'product'
): string | null {
  const {slug} = field;
  const mapper = ECOMMERCE_QUERY_PATH_MAPPER[target];
  if (mapper[slug]) {
    return mapper[slug];
  }

  return formatGQLField(field);
}

export function orderItemRelationships<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextRelationshipDef<RootTRef, EntityTRef> {
  return {
    type: typeReferenceBuilder.entityOf({
      entity: 'Entity',
      id: ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
    }),
    relativeContexts: [
      CART_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT_ID,
      ORDER_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT,
    ],
  };
}

export function orderItemBindableProperties<RootTRef, EntityTRef, ListTRef>(
  products: Collection,
  skus: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>,
  isDesigner: boolean
): BindablePropertiesDef<EntityTRef, ListTRef> {
  const defaultBindableProps: {
    [key: string]: BindablePropertyDef<EntityTRef, ListTRef>[];
  } = {
    Billing: [
      {
        id: 'order-item-price',
        type: 'CommercePrice',
        label: 'Price',
        path: [{id: '', slug: 'price', type: 'CommercePrice'}],
        queryPath: ['price'],
        meta: {required: true},
        rendering: {strategy: 'client'},
        groupLabel: 'Billing',
      },
    ],
    'Order items': [
      {
        id: 'order-item-quantity',
        type: 'Number',
        label: 'Item quantity',
        path: [{id: '', slug: 'count', type: 'Number'}],
        queryPath: ['count'],
        meta: {
          format: 'integer',
          precision: 0,
          allowNegative: false,
          minValue: null,
          maxValue: null,
          required: true,
        },
        rendering: {strategy: 'client'},
        groupLabel: 'Order items',
      },
      {
        id: 'order-item-subtotal',
        type: 'CommercePrice',
        label: 'Item subtotal',
        path: [{id: '', slug: 'rowTotal', type: 'CommercePrice'}],
        queryPath: ['rowTotal'],
        meta: {required: true},
        rendering: {strategy: 'client'},
        groupLabel: 'Order items',
      },
    ],
    Other: [
      {
        id: 'order-sku-properties',
        type: 'CommercePropTable',
        label: '',
        path: [
          {slug: 'product', type: 'ItemRef'},
          {slug: 'sku-properties', type: 'CommercePropTable'},
        ],
        queryPath: [
          'product',
          `${formatFieldSlug('sku-properties')}: skuProperties`,
        ],
        meta: {required: true},
        rendering: {strategy: 'client'},
        groupLabel: 'Other',
      },
      {
        id: 'order-sku-values',
        type: 'CommercePropTable',
        label: '',
        path: [
          {slug: 'sku', type: 'ItemRef'},
          {slug: 'sku-values', type: 'CommercePropValues'},
        ],
        queryPath: ['sku', `${formatFieldSlug('sku-values')}: skuValues`],
        meta: {required: true},
        rendering: {strategy: 'client'},
        groupLabel: 'Other',
      },
    ],
  };

  let productFields = getBindableFields(products);
  let skuFields = getBindableSkuFields(skus);
  if (isDesigner) {
    // sort fields only in Designer environment for UI reasons
    productFields = sortProductAndSkuFields(productFields);
    skuFields = sortProductAndSkuFields(skuFields);
  }

  const groupedProductFields = groupCollectionFieldsByProductSection(
    productFields,
    products
  );
  const groupedSkusFields = groupCollectionFieldsByProductSection(
    skuFields,
    skus
  );

  const productField = buildProductItemRefField(products._id || '');
  const skuField = buildSkuItemRefField(skus._id || '');

  const groupLabels = [
    ...Object.keys(
      DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION[COLLECTION_TYPES.PRODUCTS]
    ),
    'Custom fields',
    'Other',
  ];

  const bindableProperties = groupLabels.reduce<Array<any>>(
    (props, groupLabel) => {
      const productFieldGroup = groupedProductFields[groupLabel];
      const skuFieldGroup = groupedSkusFields[groupLabel];
      const defaultGroup = defaultBindableProps[groupLabel];

      if (productFieldGroup) {
        productFieldGroup.forEach((field) => {
          const fieldPath: FieldPath = [productField, field];
          const bindableProp = defineBindablePropertyFromFieldPath<
            RootTRef,
            EntityTRef,
            ListTRef
          >({
            collection: products,
            fieldPath,
            referenceBuilder,
            groupLabel,
            renderingStrategy: 'client',
            queryPath: [
              'product',
              `${formatFieldSlug(field.slug)}: ${formatGQLFieldResolver(field, 'product')}`,
            ],
          });

          if (bindableProp) {
            props.push(
              preventItemRefPropExpansion<EntityTRef, ListTRef>(bindableProp)
            );
          }
        });
      }

      if (defaultGroup) {
        props.push(...defaultGroup);
      }

      if (skuFieldGroup) {
        skuFieldGroup.forEach((field) => {
          const fieldPath: FieldPath = [skuField, field];
          const bindableProp = defineBindablePropertyFromFieldPath<
            RootTRef,
            EntityTRef,
            ListTRef
          >({
            collection: skus,
            fieldPath,
            referenceBuilder,
            groupLabel,
            renderingStrategy: 'client',
            queryPath: [
              'sku',
              `${formatFieldSlug(field.slug)}: ${formatGQLFieldResolver(field, 'sku')}`,
            ],
            // Bindings to orderItem.sku.price have been deprecated and replaced
            // with binding to orderItem.price, but we want to still build
            // queries correctly for such bindings when encountered.
            deprecated: field.slug === 'price',
          });

          if (bindableProp) {
            props.push(bindableProp);
          }
        });
      }

      return props;
    },
    []
  );

  // Provide SKU fields as deprecated bindable properties for Order Items
  // to support generating queries for product.default-sku.some-field bindings
  // that can be encountered on old commerce sites.
  const defaultSkuField = getFieldBySlug(products, DEFAULT_SKU_SLUG);
  if (defaultSkuField) {
    for (const field of skuFields) {
      const deprecatedBindableProp = defineBindablePropertyFromFieldPath<
        RootTRef,
        EntityTRef,
        ListTRef
      >({
        collection: skus,
        fieldPath: [productField, defaultSkuField, field],
        referenceBuilder,
        groupLabel: 'Other',
        renderingStrategy: 'client',
        queryPath: [
          'product',
          `${formatFieldSlug(defaultSkuField.slug)}: defaultSku`,
          `${formatFieldSlug(field.slug)}: ${formatGQLFieldResolver(field, 'sku')}`,
        ],
        deprecated: true,
      });

      if (deprecatedBindableProp) {
        bindableProperties.push(deprecatedBindableProp);
      }
    }
  }

  bindableProperties.push({
    id: 'order-item-product-full-slug',
    type: 'FullSlug',
    label: 'URL Path',
    path: [
      ...toBindingPath([productField]),
      {slug: 'fullSlug', type: 'FullSlug'},
    ],
    queryPath: ['product', 'fullSlug'],
    meta: {
      required: false,
    },
    rendering: {strategy: 'client'},
  });

  return {
    type: referenceBuilder.entityOf({
      entity: 'Entity',
      id: ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
    }),
    bindableProperties,
  };
}

function preventItemRefPropExpansion<EntityTRef, ListTRef>(
  bindableProp: BindablePropertyDef<EntityTRef, ListTRef>
): BindablePropertyDef<EntityTRef, ListTRef> {
  if (bindableProp.type === 'ItemRef') {
    const {
      id,
      meta,
      label,
      path,
      queryPath,
      rendering,
      groupLabel = null,
    } = bindableProp;
    const base =
      groupLabel != null
        ? {id, label, path, queryPath, rendering, groupLabel}
        : {id, label, path, queryPath, rendering};

    return {
      type: 'ItemRef',
      meta: {
        ...meta,
        preventExpansion: true,
      },
      ...base,
    };
  }

  return bindableProp;
}
